// Prompt 0456 — Helpers puros para extraer/serializar secciones ProseMirror.
// Extraido de puesto-panel/tabs/FichaTab.tsx y rol-funcional-panel/tabs/FichaTab.tsx
// (0454 + 0455). Ambos usaban implementaciones gemelas solo diferenciandose
// en el HEADING_TO_SECCION map — aqui se parametriza.

import type { FichaContenido, HeadingToSeccionMap, ProseMirrorNode, SeccionId } from './types';

// Placeholder que la plantilla backend inserta cuando una seccion no tiene contenido.
// Se deja con y sin acento porque ambas variantes aparecen en fichas reales.
const PENDING_TEXTS = new Set(['(Pendiente de redacción)', '(Pendiente de redaccion)']);

export function textOfNode(node: ProseMirrorNode): string {
  const content = (node.content ?? []) as ProseMirrorNode[];
  return content
    .map((c) => (typeof c.text === 'string' ? c.text : ''))
    .join('')
    .trim();
}

/**
 * Recorre un contenidoJson ProseMirror y devuelve, por cada SeccionId, el array de
 * nodos hijos del heading correspondiente (paragraph, bulletList, orderedList, etc.)
 * hasta el siguiente heading. Si todos los nodos de una seccion son placeholders
 * "(Pendiente...)", devuelve [] (para que Tiptap muestre editor vacio, no el placeholder).
 *
 * `headingMap` parametriza qué heading literal pertenece a qué SeccionId — cada panel
 * (puesto / rol) pasa el suyo porque la plantilla backend usa textos distintos.
 */
export function extractSeccionesNodos(
  ficha: FichaContenido,
  headingMap: HeadingToSeccionMap,
): Record<SeccionId, ProseMirrorNode[]> {
  const result: Partial<Record<SeccionId, ProseMirrorNode[]>> = {};
  const nodes = (ficha.contenidoJson?.content ?? []) as unknown as ProseMirrorNode[];
  let currentSection: SeccionId | null = null;
  let buffer: ProseMirrorNode[] = [];

  const flush = () => {
    if (currentSection && buffer.length > 0) {
      const allPending = buffer.every(
        (n) => n.type === 'paragraph' && PENDING_TEXTS.has(textOfNode(n)),
      );
      result[currentSection] = allPending ? [] : buffer;
    }
  };

  for (const node of nodes) {
    if (node.type === 'heading') {
      flush();
      const title = textOfNode(node);
      const key = headingMap[title];
      currentSection = key ?? null;
      buffer = [];
      continue;
    }
    if (currentSection) buffer.push(node);
  }
  flush();

  return result as Record<SeccionId, ProseMirrorNode[]>;
}

/**
 * Nodo "vacio" que Tiptap espera como contenido inicial de un editor sin texto aun.
 * Devuelto en formato doc para que useEditor lo acepte.
 */
export function nodoVacio(): ProseMirrorNode[] {
  return [{ type: 'paragraph' }];
}
